import { useCallback, useEffect, useRef } from 'react';
import type { MediaControlsDataInterface } from '../../../contexts/media-controls';
import { videoActionCommands } from '../../../utils/video-machine';

const { SCRUB, STOPSCRUBBING, PLAY, PAUSE, END, MUTE, UNMUTE } = videoActionCommands;

interface UseMediaAudioRestartHookArgs {
	trigger: boolean;
	templateControl?: MediaControlsDataInterface;
	audioCanvasControl?: MediaControlsDataInterface;
}

export function useMediaAudioRestartHook({
	trigger,
	templateControl,
	audioCanvasControl,
}: UseMediaAudioRestartHookArgs) {
	const watchedIdRef = useRef<number | string | null>(null);
	const prevTriggerRef = useRef<unknown>(trigger);

	const setWatchedId = useCallback((id: number | null) => {
		watchedIdRef.current = id;
		// because this logic depends on the element being playing, and we are assuming
		// that by setting a watched id they want that version to play, we need to set the element
		// to PLAY so that the trigger useEffect will pick it up.
		// To do this we MUTE and then UNMUTE on play so that they will not hear the old/stale version play at all
		audioCanvasControl?.send?.(MUTE, { from: 'media-audio-restart-hook' });
		audioCanvasControl?.send?.(PLAY, { from: 'media-audio-restart-hook' });
	}, [audioCanvasControl?.send]);

	const restartBothAudioAndMedia = useCallback(() => {
		templateControl?.send?.(SCRUB, { currentTime: 0, from: 'media-audio-restart-hook' });
		templateControl?.send?.(STOPSCRUBBING, { from: 'media-audio-restart-hook' });
		templateControl?.send?.(PLAY, { from: 'media-audio-restart-hook' });
		
		audioCanvasControl?.send?.(SCRUB, { currentTime: 0, from: 'media-audio-restart-hook' });
		audioCanvasControl?.send?.(STOPSCRUBBING, { from: 'media-audio-restart-hook' });
		audioCanvasControl?.send?.(UNMUTE, { from: 'media-audio-restart-hook' });
		audioCanvasControl?.send?.(PLAY, { from: 'media-audio-restart-hook' });
	}, [audioCanvasControl?.send, templateControl?.send])

	useEffect(() => {
		const prevTrigger = prevTriggerRef.current;
		prevTriggerRef.current = trigger;

		if (trigger && trigger !== prevTrigger && watchedIdRef.current !== null) {
			restartBothAudioAndMedia();
			watchedIdRef.current = null;
		}
	}, [trigger, restartBothAudioAndMedia]);

	return { setWatchedId, restartBothAudioAndMedia };
}
